import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS, SERVICES } from "@/lib/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { pathname } = useLocation();

  // Pages that start with a dark hero section — navbar should render light
  // (white) when at the top of these pages, and switch to solid/light-mode
  // once scrolled past ~80vh.
  const darkHeroPages = ["/"];
  const isDarkHero = darkHeroPages.includes(pathname);
  const overDark = isDarkHero && !scrolled;

  // Home nav is always white with teal links/logo ring (matching the CTA
  // button's accent), regardless of scroll position.
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  const navLinkClass = (isActive) =>
    `link-underline font-sans text-sm font-medium transition-colors duration-500 ${
      isActive
        ? "text-[#189A96]"
        : isHome
        ? "text-[#189A96] hover:text-[#0F2A44]"
        : overDark
        ? "text-white hover:text-[#189A96]"
        : "text-[#0F2A44] hover:text-[#189A96]"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isHome
          ? "nav-glass-light"
          : scrolled
          ? "nav-glass-light"
          : overDark
          ? "nav-glass-dark"
          : "nav-glass-light"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3"
        >
          <span className="flex items-center justify-center shrink-0 px-3 py-2">
            <img
              src={BRAND.logoFullUrl}
              alt="GoHealthe"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) =>
            l.to === "/services" ? (
              <div key={l.to} className="relative group py-6 -my-6">
                <Link
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`${navLinkClass(
                    pathname === l.to || pathname.startsWith("/services/")
                  )} inline-flex items-center gap-1.5`}
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out">
                  <div className="w-72 bg-white border border-[#0F2A44]/10 shadow-[0_20px_45px_-15px_rgba(15,42,68,0.25)] py-2">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-services-${s.slug}`}
                        className="block px-5 py-3 font-sans text-sm text-[#0F2A44] hover:bg-[#F8FAFC] hover:text-[#189A96] transition-colors duration-200"
                      >
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={navLinkClass(pathname === l.to)}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold tracking-[0.14em] uppercase transition-all duration-500 ${
              overDark
                ? "bg-[#189A96] text-white hover:bg-white hover:text-[#0F2A44]"
                : "bg-[#0F2A44] text-white hover:bg-[#189A96]"
            }`}
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden w-11 h-11 flex items-center justify-center border transition-colors duration-500 ${
              overDark && !isHome
                ? "border-white/30 text-white"
                : "border-[#0F2A44]/20 text-[#0F2A44]"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="lg:hidden bg-white border-t border-[#E2E8F0]"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) =>
              l.to === "/services" ? (
                <div key={l.to}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={l.to}
                      data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                      className="font-serif text-3xl text-[#0F2A44]"
                    >
                      {l.label}
                    </Link>
                    <button
                      type="button"
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      aria-label="Toggle services submenu"
                      data-testid="nav-mobile-services-toggle"
                      className="w-10 h-10 flex items-center justify-center text-[#0F2A44]"
                    >
                      <ChevronDown
                        className={`w-5 h-5 transition-transform duration-300 ${
                          mobileServicesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className="mt-3 flex flex-col gap-1 border-l border-[#0F2A44]/12 pl-4">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-mobile-services-${s.slug}`}
                          className="py-2.5 font-sans text-base text-[#0F2A44]/70"
                        >
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className="font-serif text-3xl text-[#0F2A44]"
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center justify-between px-5 py-4 bg-[#0F2A44] text-white text-xs font-semibold tracking-[0.14em] uppercase"
            >
              Request a Quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
